import type { ReactNode, RefObject } from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { styled } from "@schaeffler-commerce/design-system";
import type { AppDispatch } from "~/app/store";
import type { PageMetadata, PageServerMessageData, PageSlotData } from "~/app/api/pageData";
import { getAnonymousGlobalSlotsUserUid, globalSlotsQueryOptions, hasBrowserPreviewTicket } from "~/app/api/pageData";
import { useSessionState } from "~/app/providers/SessionProvider";
import { PageMetadataProvider } from "~/app/config/pageMetadataContext";
import { useCommerceContext } from "~/app/config/useSession";
import { normalizeServerMessage, takeStashedRedirectMessages } from "~/app/api/pageData/normalizeServerMessage";
import { addMessage, addMessages } from "~/features/toasts/redux/actions";
import { AssistedServiceBar } from "~/features/assistedService/components/AssistedServiceBar/AssistedServiceBar";
import GlobalMessage from "~/features/navigation/components/GlobalMessage";
import { RegisteredComponent, renderCmsSlotComponent } from "./cmsSlotRendering";
import { PageTemplateRenderer } from "./pageTemplates/PageTemplateRenderer";
import { HeadManager } from "./HeadManager";
import { SmartEditBodyClasses } from "./SmartEditBodyClasses";
import { SmartEditContentRefresh } from "./SmartEditContentRefresh";
import { SmartEditFullPageNavigation } from "./SmartEditFullPageNavigation";
import { SmartEditWrapper } from "./SmartEditWrapper";
import { StorefrontCampaignBanners } from "./StorefrontCampaignBanners";
import type { StorefrontShellVariant } from "./types";
import { ShellHeaderRoot } from "./ShellHeaderRoot";
import { ShellFooterRoot } from "./ShellFooterRoot";
import { consumePostLoginCartErrorCookie } from "./postLoginCartErrorCookie";
import { SessionExpirePopupContainer } from "~/features/auth/session/useSessionExpirePopup";

const SEARCH_PAGE_TEMPLATE_UIDS = new Set([
  "SearchResultsListPageTemplate",
  "SearchResultsGridPageTemplate",
  "SearchResultsEmptyPageTemplate",
]);

interface StorefrontShellFrameProps {
  variant: StorefrontShellVariant;
  metadata: PageMetadata;
  header?: ReactNode;
  hideFooter?: boolean;
  children: ReactNode;
}

function CmsSlot({
  slots,
  position,
  metadata,
  className,
}: {
  slots: PageSlotData[];
  position: string;
  metadata: PageMetadata;
  className?: string;
}) {
  const slot = slots.find((candidate) => candidate.position === position);
  if (!slot || (slot.components.length === 0 && !slot.smartEdit)) {
    return null;
  }

  // The outer layout <div> (e.g. `container` on TopHeaderSlot) must render in both
  // normal and SmartEdit modes. The SmartEdit slot wrapper sits inside it so normal
  // storefront markup stays byte-identical (SmartEditWrapper returns a fragment when
  // not in SmartEdit / no smartEdit payload). Mirrors PageSlotLookup.renderSlot, so
  // chrome/global slots (Footer, TopHeaderSlot, …) are SmartEdit-editable slots too.
  return (
    <div className={className}>
      <SmartEditWrapper kind="slot" smartEdit={slot.smartEdit}>
        {slot.components.map((component) => (
          <CmsSlotComponentWrapper
            key={component.uid ?? `${slot.position}-${component.typeCode}`}
            component={component}
            metadata={metadata}
          />
        ))}
      </SmartEditWrapper>
    </div>
  );
}

function CmsSlotComponentWrapper({
  component,
  metadata,
}: {
  component: PageSlotData["components"][number];
  metadata: PageMetadata;
}) {
  return <>{renderCmsSlotComponent(component, metadata)}</>;
}

function ShellHeader({
  metadata,
  globalSlots,
  header,
  stickyHeaderRef,
}: {
  metadata: PageMetadata;
  globalSlots: PageSlotData[];
  header?: ReactNode;
  stickyHeaderRef: RefObject<HTMLElement | null>;
}) {
  const usesStandardHeader = header === undefined;
  const showEdit = Boolean(useCommerceContext()?.showEdit);
  const isSearchPage = metadata.templateUid ? SEARCH_PAGE_TEMPLATE_UIDS.has(metadata.templateUid) : false;

  return (
    <>
      <AssistedServiceBar />
      <div hidden>
        <div id="cartHeader">
          <CmsSlot slots={globalSlots} position="MiniCart" metadata={metadata} />
        </div>
      </div>
      <ShellHeaderRoot
        ref={stickyHeaderRef}
        id="root_mainHeader"
        className="scha__main-header js-header-offset"
        hasCustomHeader={!usesStandardHeader}
      >
        <div id="reactToastContainer" />
        {!usesStandardHeader && <GlobalMessage />}
        <RegisteredComponent name="RegionSelectionBanner" required={false} />
        {usesStandardHeader ? (
          <>
            <CmsSlot slots={globalSlots} position="TopHeaderSlot" metadata={metadata} className="container" />
            <CmsSlot slots={globalSlots} position="NavigationBar" metadata={metadata} />
          </>
        ) : null}
        <StorefrontCampaignBanners metadata={metadata} />
        {usesStandardHeader ? (
          <>
            <RegisteredComponent
              name="HeaderNavigation"
              props={{ isSearchPage, showEdit, includeSessionExpirePopup: false }}
            />
            <div className="container" id="root_breadcrumbs">
              {metadata.breadcrumbs.length ? (
                <RegisteredComponent name="Breadcrumbs" props={{ items: metadata.breadcrumbs }} required={false} />
              ) : null}
            </div>
          </>
        ) : (
          header
        )}
      </ShellHeaderRoot>
      <RegisteredComponent name="AffiliateSalesPartnerBanner" required={false} />
      <RegisteredComponent name="AffiliateSalesPartnerPopup" required={false} />
      <RegisteredComponent name="BrowserNotSupportedPopup" required={false} />
    </>
  );
}

function ShellFooter({ metadata, globalSlots }: { metadata: PageMetadata; globalSlots: PageSlotData[] }) {
  return (
    <>
      <ShellFooterRoot className="jsp__footer scha__screen-only">
        <CmsSlot slots={globalSlots} position="Footer" metadata={metadata} />
      </ShellFooterRoot>
      <RegisteredComponent name="BottomNavigationComponent" required={false} />
    </>
  );
}

function useSeedServerMessages(serverMessages: PageServerMessageData[] = []) {
  const dispatch = useDispatch<AppDispatch>();
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) {
      return;
    }

    // Redirect messages stashed by the previous SPA instance (FlashMap replacement) take
    // precedence, then this page's own metadata server messages.
    const stashed = takeStashedRedirectMessages();
    const allMessages = [...stashed, ...serverMessages];
    if (allMessages.length === 0) {
      return;
    }

    seeded.current = true;
    dispatch(addMessages(allMessages.map(normalizeServerMessage)));
  }, [dispatch, serverMessages]);
}

function useStickyHeaderHeight(enabled: boolean) {
  const stickyHeaderRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const stickyHeader = stickyHeaderRef.current;
    const content = contentRef.current;
    if (!enabled || !stickyHeader || !content) {
      return;
    }

    const publishHeight = () => {
      content.style.setProperty(
        "--configurator-sticky-header-height",
        `${Math.ceil(stickyHeader.getBoundingClientRect().height)}px`,
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(stickyHeader);

    return () => {
      observer.disconnect();
      content.style.removeProperty("--configurator-sticky-header-height");
    };
  }, [enabled]);

  return { stickyHeaderRef, contentRef };
}

function usePostLoginCartError(variant: StorefrontShellVariant) {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (variant === "standard" && consumePostLoginCartErrorCookie()) {
      dispatch(addMessage("error", "addToCart.failure", "translation"));
    }
  }, [dispatch, variant]);
}

/**
 * Global slots come from one cached structure query keyed by user and Sold-To account context and are
 * reused across page navigations. Route metadata remains the fallback whenever the query cannot
 * provide slots (session unresolved, request in flight, transport error), so fallback rendering is
 * identical to a shell without the query.
 */
function useGlobalSlots(variant: StorefrontShellVariant, metadata: PageMetadata): PageSlotData[] {
  const { availability, data: session } = useSessionState();
  const resolvedSession = availability === "ready" ? session : null;
  // The backend contract always populates SessionData.user, but a malformed payload must degrade
  // to the anonymous identity rather than crash the shell.
  const userUid = resolvedSession ? resolvedSession.user?.uid ?? getAnonymousGlobalSlotsUserUid() : "";
  const accountKey = resolvedSession?.commerceContext?.selectedAccount?.uid ?? "";

  const globalSlots = useQuery({
    ...globalSlotsQueryOptions(userUid, accountKey, hasBrowserPreviewTicket()),
    enabled: resolvedSession !== null && variant === "standard",
  });

  return globalSlots.data?.slots ?? metadata.slots.filter((slot) => slot.scope === "global");
}

export function StorefrontShellFrame({
  variant,
  metadata,
  header,
  hideFooter = false,
  children,
}: StorefrontShellFrameProps) {
  useSeedServerMessages(metadata.serverMessages);
  usePostLoginCartError(variant);

  const isStandard = variant === "standard";
  const hasCustomHeader = header !== undefined;
  const globalSlots = useGlobalSlots(variant, metadata);
  const { stickyHeaderRef, contentRef } = useStickyHeaderHeight(isStandard && hasCustomHeader);

  return (
    <PageMetadataProvider value={metadata}>
      <SmartEditBodyClasses />
      <SmartEditContentRefresh />
      <SmartEditFullPageNavigation />
      <HeadManager metadata={metadata} />
      <SessionExpirePopupContainer />
      <ShellMain className="scha__main" data-storefront-shell-variant={variant}>
        {isStandard && (
          <ShellHeader
            metadata={metadata}
            globalSlots={globalSlots}
            header={header}
            stickyHeaderRef={stickyHeaderRef}
          />
        )}
        {!isStandard && (
          <div id="reactToastContainer">
            <GlobalMessage />
          </div>
        )}
        <ShellMainContainer ref={contentRef} className="container scha__main-container">
          <PageTemplateRenderer metadata={metadata}>{children}</PageTemplateRenderer>
        </ShellMainContainer>
        {isStandard && !hideFooter && <ShellFooter metadata={metadata} globalSlots={globalSlots} />}
      </ShellMain>
    </PageMetadataProvider>
  );
}

// Migrated from style.less (.scha__main / .scha__main-container). The marker
// classes stay rendered; no external selector targets them (verified), but
// the classes predate the migration and are kept for parity.
// Do not add overflow hidden on the main element. It breaks position: sticky.
const ShellMain = styled("main")({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
});

const ShellMainContainer = styled("div")({
  flex: "1 1 auto",
  position: "relative",
  zIndex: 1,
});
